import React, { useCallback, useMemo, useState } from "react";

import { Box, Input, SectionHeader } from "arkh";

import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import {
  OrganisationRoleType,
  OrgUserInviteMutation,
} from "./__generated__/OrgUserInviteMutation.graphql";

import { FormattedMessage, useIntl } from "react-intl";
import { UserLoggedInNavigatorNavigationProp } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useAlert } from "../../../../common/Alert";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { OrgUserInviteQuery } from "./__generated__/OrgUserInviteQuery.graphql";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../../../common/navigationHeaderOptions";
import { UserTypeSelect } from "./UserTypeSelect";
import {
  WorkspaceRole,
  WorkspaceRolesSelector,
} from "./WorkspaceRolesSelector";

type ParamList = {
  OrgUserInviteScreen: object;
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgUserInvite = withWebModal(() => {
  const intl_tokens = useIntlTokens();

  const getOptions = useNavigationHeaderOptions();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="OrgUserInviteScreen"
        component={OrgUserInviteScreen}
        options={({ navigation }) =>
          getOptions({
            type: "CREATE",
            onCancel: () => {
              navigation.goBack();
            },
            title: intl_tokens.InviteUser,
            onSave: () => {},
            saveLoading: false,
            disabled: true,
          })
        }
      />
    </Stack.Navigator>
  );
});

type OrgUserInviteScreenProps = {
  route: RouteProp<ParamList, "OrgUserInviteScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgUserInvite">,
    NativeStackNavigationProp<ParamList, "OrgUserInviteScreen">
  >;
};

function OrgUserInviteScreen(props: OrgUserInviteScreenProps) {
  const intl_tokens = useIntlTokens();
  const [email, setEmail] = useState<string>("");

  const [inviteUsers, inviteUsersLoading] = useMutation<OrgUserInviteMutation>(
    graphql`
      mutation OrgUserInviteMutation(
        $users: [OrgUserInvite!]!
        $orgRole: OrganisationRoleType!
        $roles: [UserWorkspaceRoleInput!]!
      ) {
        organisation {
          inviteUsers(
            users: $users
            orgRole: $orgRole
            workspaceRoles: $roles
          ) {
            ... on OrgUserInviteSuccessResult {
              __typename
              user {
                id
              }
            }
            ... on OrgUserFailedResult {
              __typename
              email
              reason
            }
          }
        }
      }
    `
  );

  const intl = useIntl();
  const alert = useAlert();

  const data = useLazyLoadQuery<OrgUserInviteQuery>(
    graphql`
      query OrgUserInviteQuery {
        organisation @required(action: THROW) {
          workspaces {
            ...OrgUserEditAccessSelect_workspaces
            ...WorkspaceRolesSelector_workspaces
          }
        }
      }
    `,
    {},
    {
      fetchPolicy: "network-only",
    }
  );

  const [roles, setRoles] = useState<WorkspaceRole[]>([]);
  const [orgRole, setOrgRole] =
    useState<OrganisationRoleType>("WORKSPACE_USER");

  const onInviteUser = useCallback(() => {
    inviteUsers({
      variables: {
        users: email.split(",").map((e) => ({ email: e.trim() })),
        orgRole,
        roles: roles
          .map((r) => ({
            role: r.roleType ?? "ADMIN",
            workspaceId: r.workspace?.id!,
          }))
          .filter((s) => s.workspaceId),
      },
      onCompleted: (data) => {
        if (
          data.organisation.inviteUsers
            ?.map((r) => r.__typename)
            .includes("OrgUserFailedResult")
        ) {
          alert.alert(
            intl.formatMessage({ defaultMessage: "Failed to invite users" }),
            data.organisation.inviteUsers.reduce(
              (acc, cur) =>
                cur.__typename === "OrgUserFailedResult"
                  ? `${acc}\n${cur.email}`
                  : acc,
              ""
            )
          );
        } else {
          props.navigation.goBack();
        }
      },
    });
  }, [inviteUsers, email, roles, orgRole, alert, intl, props.navigation]);

  const disabled = useMemo(() => {
    if (
      orgRole === "WORKSPACE_USER" &&
      roles.filter((r) => r.workspace).length === 0
    ) {
      return true;
    }
    return email.length === 0;
  }, [email, roles, orgRole]);

  useNavigationHeader({
    type: "CREATE",
    title: intl_tokens.InviteUser,
    createButtonLabel: intl_tokens.Invite,
    defaultContentStyle: true,
    onCancel: () => {
      props.navigation.goBack();
    },
    disabled,
    onSave: () => {
      onInviteUser();
    },

    saveLoading: inviteUsersLoading,
  });

  return (
    <Box space="medium">
      <Input
        value={email}
        onChangeText={setEmail}
        placeholder={intl_tokens.Email}
        textContentType="emailAddress"
      />

      <Box spaceBottom="large" />
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Access"
            description="OrgUserInvite Access section header"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <UserTypeSelect
        selected={orgRole}
        onSelect={(value) => setOrgRole(value)}
      />
      <Box spaceBottom="large" />
      {orgRole === "WORKSPACE_USER" ? (
        <WorkspaceRolesSelector
          roles={roles}
          setRoles={setRoles}
          workspaces={data.organisation.workspaces}
        />
      ) : null}
    </Box>
  );
}
